import type { JSX } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CollapsibleTrigger } from '@/components/ui/collapsible';
import type { Experience, ExperienceStatus } from '../types/experience.types';
import { formatDateInfo, calculateDuration } from '../types/experience.types';
import { ExperienceStatusBadge } from './ExperienceStatusBadge';
import { ExperienceActions } from './ExperienceActions';

export function ExperienceCardHeader({
  experience,
  onEdit,
  onDelete,
  onStatusChange,
  showTrigger = false,
}: {
  experience: Experience;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (status: ExperienceStatus) => void;
  showTrigger?: boolean;
}): JSX.Element {
  return (
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <div className="flex flex-col gap-1">
        {showTrigger ? (
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="font-semibold text-lg text-left hover:underline cursor-pointer"
            >
              {experience.companyName}
            </button>
          </CollapsibleTrigger>
        ) : (
          <span className="font-semibold text-lg">{experience.companyName}</span>
        )}
        <span className="text-sm text-muted-foreground">{experience.jobTitle}</span>
      </div>
      <div className="flex items-center gap-2">
        <ExperienceStatusBadge status={experience.status} />
        <ExperienceActions
          experience={experience}
          onEdit={onEdit}
          onDelete={onDelete}
          onStatusChange={onStatusChange}
        />
      </div>
    </CardHeader>
  );
}

export function ExperienceCardBody({
  experience,
  isExpanded,
  showTrigger,
}: {
  experience: Experience;
  isExpanded: boolean;
  showTrigger: boolean;
}): JSX.Element {
  const period = `${formatDateInfo(experience.startDate)} - ${experience.isOngoing ? 'Present' : formatDateInfo(experience.endDate)}`;
  const duration = calculateDuration(
    experience.startDate,
    experience.endDate,
    experience.isOngoing
  );

  return (
    <CardContent>
      <div className="flex flex-col gap-2 text-sm">
        <span className="text-muted-foreground">{period}</span>
        {duration && <span className="text-muted-foreground">{duration}</span>}
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {experience.isFullTime ? 'Full-time' : 'Part-time'}
          </Badge>
          {experience.isContractor && (
            <Badge variant="outline" className="text-xs">
              Contractor
            </Badge>
          )}
        </div>
      </div>
      {showTrigger && (
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="mt-2 px-0">
            {isExpanded ? (
              <ChevronDown className="mr-1 h-4 w-4" />
            ) : (
              <ChevronRight className="mr-1 h-4 w-4" />
            )}
            Verifications
          </Button>
        </CollapsibleTrigger>
      )}
    </CardContent>
  );
}
